import { ref, watch, computed, onMounted } from '@vue/composition-api';
import Handsontable from 'handsontable';
import 'handsontable/dist/handsontable.full.css';
import store from '@/store';
import registerFbrItemCellType from '../../utils/fbrItemCell.js';

// Register the custom cell type
registerFbrItemCellType(Handsontable);

// Validation function for positive values
const validatePositiveValue = (row, value, callback) => {
  if ((row && row.itemId && value < 0) || isNaN(value)) {
    callback(false);
    return;
  }
  callback(true);
};

// Format number for display
const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Get empty row template
const getEmptyRow = (furtherTaxPercent) => {
  return {
    itemId: null,
    itemHSCode: '',
    qty: 0,
    unit: null,
    uom: 0,
    rate: 0,
    grossAmount: 0,
    discountPercent: 0,
    discountAmount: 0,
    netAmount: 0,
    salesTaxPercent: 0,
    salesTaxAmount: 0,
    sroScheduleNo: '',
    sroItemSerialNo: '',
    notifiedValue: 0,
    furtherTaxPercent: furtherTaxPercent,
    furtherTaxAmount: 0,
    addSalesTaxPercent: 0,
    addSalesTaxAmount: 0,
    valWithoutTax: 0,
    totalTax: 0,
    tax236GValue: 0,
    valIncludingTax: 0,
  };
};

export default function useInvoiceDetails(props, { emit }) {
  const businessID = computed(() => store.state.user.businessID);

  const initialFurtherTaxPercent = computed(() => props.furtherTaxPercent);

  // Reactive references
  const tableRef = ref(null);
  const hotInstance = ref(null);

  const showCreateItemModal = ref(false);
  const showAddTaxCol = ref(false);

  // Initialize table data
  const initializeData = (data) => {
    if (!data || data.length === 0) return [getEmptyRow(props.furtherTaxPercent || 0)];
    return data.map((row) => {
      const valWithoutTax = parseFloat(row.valWithoutTax || 0);
      const totalTax = parseFloat(row.totalTax || 0);
      const tax236GValue = parseFloat(row.tax236GValue || 0);
      const valIncludingTax = parseFloat((((valWithoutTax + totalTax) * tax236GValue) / 100).toFixed(2));
      return {
        ...row,
        valIncludingTax,
      };
    });
  };

  const tableData = ref(initializeData(props.value));

  // Column definitions
  const getColumns = () => [
    {
      data: 'item',
      type: 'fbrItem',
      title: 'Item',
      width: 220,
    },
    { data: 'itemHSCode', title: 'HS Code', type: 'text', width: 120, readOnly: false },
    {
      data: 'unit',
      title: 'UOM',
      type: 'text',
      readOnly: true,
    },
    {
      data: 'qty',
      title: 'Qty',
      type: 'numeric',
      width: 80,
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'rate',
      title: 'Rate',
      type: 'numeric',
      width: 100,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'grossAmount',
      title: 'Gross Amount',
      type: 'numeric',
      width: 120,
      readOnly: true,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'discountPercent',
      title: 'Discount %',
      type: 'numeric',
      width: 100,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'discountAmount',
      title: 'Discount Amount',
      type: 'numeric',
      width: 120,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'netAmount',
      title: 'Net Amount',
      type: 'numeric',
      width: 120,
      readOnly: true,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'salesTaxPercent',
      title: 'Sales Tax %',
      type: 'numeric',
      width: 100,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'salesTaxAmount',
      title: 'Sales Tax Amount',
      type: 'numeric',
      width: 120,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'sroScheduleNo',
      title: 'SRO Schedule Number',
      type: 'dropdown',
      source: ['297(I)/2023-Table-II', '297(I)/2023-Table-I'],
      width: 120,
      validator: function (value, callback) {
        const row = this.instance.getSourceDataAtRow(this.row);

        if (row && row.item && row.salesTaxPercent < 18) {
          if (!value || value.trim() === '') {
            callback(false);
            return;
          }
        }
        callback(true);
      },
    },
    {
      data: 'sroItemSerialNo',
      title: 'SRO Serial Number',
      type: 'text',
      width: 120,
      validator: function (value, callback) {
        const row = this.instance.getSourceDataAtRow(this.row);

        if (row && row.item && (isNaN(row.salesTaxPercent) || row.salesTaxPercent < 18)) {
          if (!value || value.trim() === '') {
            callback(false);
            return;
          }
        }
        callback(true);
      },
    },
    {
      data: 'notifiedValue',
      title: 'Fixed/ Notified',
      type: 'numeric',
      width: 120,
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'furtherTaxPercent',
      title: 'Further Tax %',
      type: 'numeric',
      width: 100,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'furtherTaxAmount',
      title: 'Further Tax Amount',
      type: 'numeric',
      width: 120,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'addSalesTaxPercent',
      title: 'Add Sales Tax %',
      type: 'numeric',
      width: 120,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'addSalesTaxAmount',
      title: 'Add Sales Tax Amount',
      type: 'numeric',
      width: 140,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'valWithoutTax',
      title: 'Value Without Tax',
      type: 'numeric',
      width: 120,
      readOnly: true,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'totalTax',
      title: 'Total Tax',
      type: 'numeric',
      width: 100,
      readOnly: true,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'tax236GValue',
      title: '236G/H %',
      type: 'numeric',
      width: 100,
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
    {
      data: 'valIncludingTax',
      title: '236G/H Amount',
      type: 'numeric',
      width: 140,
      readOnly: false,
      // numericFormat: { pattern: '0,0.00000' },
      validator: function (value, callback) {
        validatePositiveValue(this.instance.getSourceDataAtRow(this.row), value, callback);
      },
    },
  ];

  // Calculate totals for column headers
  const calculateColumnTotals = () => {
    const dataRows = tableData.value.filter((row) => row.item); // Only rows with items
    const totals = {
      qty: 0,
      grossAmount: 0,
      discountAmount: 0,
      netAmount: 0,
      salesTaxAmount: 0,
      furtherTaxAmount: 0,
      addSalesTaxAmount: 0,
      valWithoutTax: 0,
      totalTax: 0,
      valIncludingTax: 0,
    };

    dataRows.forEach((row) => {
      totals.qty += parseFloat(row.qty || 0);
      totals.grossAmount += parseFloat(row.grossAmount || 0);
      totals.discountAmount += parseFloat(row.discountAmount || 0);
      totals.netAmount += parseFloat(row.netAmount || 0);
      totals.salesTaxAmount += parseFloat(row.salesTaxAmount || 0);
      totals.furtherTaxAmount += parseFloat(row.furtherTaxAmount || 0);
      totals.addSalesTaxAmount += parseFloat(row.addSalesTaxAmount || 0);
      totals.valWithoutTax += parseFloat(row.valWithoutTax || 0);
      totals.totalTax += parseFloat(row.totalTax || 0);
      totals.valIncludingTax += parseFloat(row.valIncludingTax || 0);
    });

    return totals;
  };

  // Calculate row values
  const calculateRow = (rowIndex) => {
    const row = tableData.value[rowIndex];

    if (!row) return;

    // Calculate amounts
    row.grossAmount = (row.qty || 0) * (row.rate || 0);
    row.discountAmount = (row.grossAmount * (row.discountPercent || 0)) / 100;
    row.netAmount = row.grossAmount - row.discountAmount;

    // Calculate taxes
    row.salesTaxAmount = (row.netAmount * (row.salesTaxPercent || 0)) / 100;
    row.furtherTaxAmount = (row.netAmount * (row.furtherTaxPercent || 0)) / 100;
    row.addSalesTaxAmount = (row.netAmount * (row.addSalesTaxPercent || 0)) / 100;

    row.valWithoutTax = row.netAmount;
    row.totalTax = row.salesTaxAmount + row.furtherTaxAmount + row.addSalesTaxAmount;
    
    // Calculate 236G/H Amount
    const baseAmountFor236 = row.valWithoutTax + row.totalTax;
    row.valIncludingTax = parseFloat(((baseAmountFor236 * (row.tax236GValue || 0)) / 100).toFixed(2));

    // Update table
    if (hotInstance.value) {
      hotInstance.value.setDataAtRowProp(rowIndex, 'itemHSCode', row.itemHSCode);
      hotInstance.value.setDataAtRowProp(rowIndex, 'rate', row.rate);
      hotInstance.value.setDataAtRowProp(rowIndex, 'grossAmount', row.grossAmount);
      hotInstance.value.setDataAtRowProp(rowIndex, 'discountAmount', row.discountAmount);
      hotInstance.value.setDataAtRowProp(rowIndex, 'netAmount', row.netAmount);
      hotInstance.value.setDataAtRowProp(rowIndex, 'salesTaxPercent', row.salesTaxPercent);
      hotInstance.value.setDataAtRowProp(rowIndex, 'salesTaxAmount', row.salesTaxAmount);
      hotInstance.value.setDataAtRowProp(rowIndex, 'notifiedValue', row.notifiedValue);
      hotInstance.value.setDataAtRowProp(rowIndex, 'furtherTaxAmount', row.furtherTaxAmount);
      hotInstance.value.setDataAtRowProp(rowIndex, 'addSalesTaxAmount', row.addSalesTaxAmount);
      hotInstance.value.setDataAtRowProp(rowIndex, 'valWithoutTax', row.valWithoutTax);
      hotInstance.value.setDataAtRowProp(rowIndex, 'totalTax', row.totalTax);
      hotInstance.value.setDataAtRowProp(rowIndex, 'tax236GValue', row.tax236GValue || 0);
      hotInstance.value.setDataAtRowProp(rowIndex, 'valIncludingTax', row.valIncludingTax);

      // Refresh column headers to update totals
      hotInstance.value.render();
    }
  };

  // Initialize Handsontable
  const initTable = () => {
    if (!tableRef.value) return;

    const columns = getColumns();

    hotInstance.value = new Handsontable(tableRef.value, {
      data: tableData.value,
      columns: columns,
      colHeaders: columns.map((col) => col.title),
      rowHeaders: true,
      height: 'auto',
      width: '100%',
      licenseKey: 'non-commercial-and-evaluation',
      stretchH: 'none',

      fixedColumnsStart: 1,
      minSpareRows: 1,
      contextMenu: ['row_above', 'row_below', 'remove_row'],
      manualColumnResize: true,
      manualRowResize: false,
      columnSorting: false,
      autoWrapRow: false,
      autoWrapCol: false,
      wordWrap: false,
      hiddenColumns: {
        columns: showAddTaxCol.value ? [] : [16, 17],
      },

      beforeChange: function (changes, source) {
        if (source === 'loadData') return;

        for (let i = 0; i < changes.length; i++) {
          const [row, prop, oldValue, newValue] = changes[i];

          // Validate numeric fields
          if (
            [
              'qty',
              'rate',
              'discountPercent',
              'discountAmount',
              'salesTaxPercent',
              'salesTaxAmount',
              'notifiedValue',
              'furtherTaxPercent',
              'furtherTaxAmount',
              'addSalesTaxPercent',
              'addSalesTaxAmount',
              'tax236GValue',
              'valIncludingTax',
            ].includes(prop)
          ) {
            if (newValue !== null && newValue !== '' && isNaN(parseFloat(newValue))) {
              return false; // Prevent invalid numeric input
            }
          }
        }
      },
      afterChange: function (changes, source) {
        if (source === 'loadData') return;

        changes.forEach(([row, prop, oldValue, newValue]) => {
          if (oldValue !== newValue) {
            switch (prop) {
              case 'item':
                if (!newValue) {
                  tableData.value[row].itemHSCode = '';
                  tableData.value[row].unit = '';
                  tableData.value[row].uom = 0;
                  tableData.value[row].rate = 0;
                } else {
                  tableData.value[row].itemId = newValue.id;
                  tableData.value[row].itemHSCode = newValue.HSCode;
                  tableData.value[row].unit = newValue.unit;
                  tableData.value[row].uom = newValue.unitID;
                  tableData.value[row].rate = newValue.tradePrice;
                  tableData.value[row].qty = 0;
                  tableData.value[row].discountPercent = 0;
                  tableData.value[row].furtherTaxPercent = initialFurtherTaxPercent.value;
                  tableData.value[row].salesTaxPercent = newValue.salesTaxPercent;
                  tableData.value[row].addSalesTaxPercent = newValue.addSalesTaxPercent;
                  tableData.value[row].sroScheduleNo = '';
                  tableData.value[row].sroItemSerialNo = '';
                }
                break;

              case 'itemHSCode':
                // Allow manual editing of HS Code
                tableData.value[row][prop] = newValue;
                break;

              case 'sroScheduleNo':
              case 'sroItemSerialNo':
                // Handle SRO fields
                tableData.value[row][prop] = newValue;
                break;

              // Handle percentage to amount relationships
              case 'discountPercent':
                tableData.value[row][prop] = newValue;
                // Calculate discount amount from percentage
                const netAmount = tableData.value[row].grossAmount || 0;
                const discountAmount = (netAmount * newValue) / 100;
                tableData.value[row].discountAmount = discountAmount;
                // Update the cell in the table
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'discountAmount', discountAmount);
                }
                break;

              case 'discountAmount':
                tableData.value[row][prop] = newValue;
                // Calculate discount percentage from amount
                const grossAmount = tableData.value[row].grossAmount || 0;
                const discountPercent = grossAmount > 0 ? (newValue / grossAmount) * 100 : 0;
                tableData.value[row].discountPercent = discountPercent;
                // Update the cell in the table
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'discountPercent', discountPercent);
                }
                break;

              case 'salesTaxPercent':
                tableData.value[row][prop] = newValue;
                // Calculate sales tax amount from percentage
                const netAmountForSalesTax = tableData.value[row].netAmount || 0;
                const salesTaxAmount = (netAmountForSalesTax * newValue) / 100;
                tableData.value[row].salesTaxAmount = salesTaxAmount;
                // Update the cell in the table
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'salesTaxAmount', salesTaxAmount);
                }
                break;

              case 'salesTaxAmount':
                tableData.value[row][prop] = newValue;
                // Calculate sales tax percentage from amount
                const netAmountForSalesTaxCalc = tableData.value[row].netAmount || 0;
                const salesTaxPercent =
                  netAmountForSalesTaxCalc > 0 ? (newValue / netAmountForSalesTaxCalc) * 100 : 0;
                tableData.value[row].salesTaxPercent = salesTaxPercent;
                // Update the cell in the table
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'salesTaxPercent', salesTaxPercent);
                }
                break;

              case 'furtherTaxPercent':
                tableData.value[row][prop] = newValue;
                // Calculate further tax amount from percentage
                const netAmountForFurtherTax = tableData.value[row].netAmount || 0;
                const furtherTaxAmount = (netAmountForFurtherTax * newValue) / 100;
                tableData.value[row].furtherTaxAmount = furtherTaxAmount;
                // Update the cell in the table
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'furtherTaxAmount', furtherTaxAmount);
                }
                break;

              case 'furtherTaxAmount':
                tableData.value[row][prop] = newValue;
                // Calculate further tax percentage from amount
                const netAmountForFurtherTaxCalc = tableData.value[row].netAmount || 0;
                const furtherTaxPercent =
                  netAmountForFurtherTaxCalc > 0
                    ? (newValue / netAmountForFurtherTaxCalc) * 100
                    : 0;
                tableData.value[row].furtherTaxPercent = furtherTaxPercent;
                // Update the cell in the table
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'furtherTaxPercent', furtherTaxPercent);
                }
                break;

              case 'addSalesTaxPercent':
                tableData.value[row][prop] = newValue;
                // Calculate additional sales tax amount from percentage
                const netAmountForAddTax = tableData.value[row].netAmount || 0;
                const addSalesTaxAmount = (netAmountForAddTax * newValue) / 100;
                tableData.value[row].addSalesTaxAmount = addSalesTaxAmount;
                // Update the cell in the table
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'addSalesTaxAmount', addSalesTaxAmount);
                }
                break;

              case 'addSalesTaxAmount':
                tableData.value[row][prop] = newValue;
                // Calculate additional sales tax percentage from amount
                const netAmountForAddTaxCalc = tableData.value[row].netAmount || 0;
                const addSalesTaxPercent =
                  netAmountForAddTaxCalc > 0 ? (newValue / netAmountForAddTaxCalc) * 100 : 0;
                tableData.value[row].addSalesTaxPercent = addSalesTaxPercent;
                // Update the cell in the table
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'addSalesTaxPercent', addSalesTaxPercent);
                }
                break;

              case 'tax236GValue':
                tableData.value[row][prop] = newValue;
                const baseFor236 = (tableData.value[row].valWithoutTax || 0) + (tableData.value[row].totalTax || 0);
                const valIncTax = parseFloat(((baseFor236 * newValue) / 100).toFixed(2));
                tableData.value[row].valIncludingTax = valIncTax;
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'valIncludingTax', valIncTax);
                }
                break;

              case 'valIncludingTax':
                tableData.value[row][prop] = newValue;
                const baseFor236Calc = (tableData.value[row].valWithoutTax || 0) + (tableData.value[row].totalTax || 0);
                const val236G = baseFor236Calc > 0 ? parseFloat(((newValue / baseFor236Calc) * 100).toFixed(4)) : 0;
                tableData.value[row].tax236GValue = val236G;
                if (hotInstance.value) {
                  hotInstance.value.setDataAtRowProp(row, 'tax236GValue', val236G);
                }
                break;

              default:
                // Update local data
                tableData.value[row][prop] = newValue;
                break;
            }

            // Recalculate row for fields that affect other calculations
            if (
              [
                'item',
                'qty',
                'rate',
                'discountPercent',
                'discountAmount',
                'salesTaxPercent',
                'salesTaxAmount',
                'furtherTaxPercent',
                'furtherTaxAmount',
                'addSalesTaxPercent',
                'addSalesTaxAmount',
                'tax236GValue',
                'valIncludingTax',
              ].includes(prop)
            ) {
              calculateRow(row);
            }
          }
        });

        // Emit changes
        hotInstance.value.render();
      },
      afterGetColHeader: function (col, TH) {
        const totals = calculateColumnTotals();
        const columnTitles = [
          'Item',
          'HS Code',
          'UOM',
          'Qty',
          'Rate',
          'Gross Amount',
          'Discount %',
          'Discount Amount',
          'Net Amount',
          'Sales Tax %',
          'Sales Tax Amount',
          'SRO Schedule Number',
          'SRO Serial Number',
          'Fixed/ Notified',
          'Further Tax %',
          'Further Tax Amount',
          'Add Sales Tax %',
          'Add Sales Tax Amount',
          'Value Without Tax',
          'Total Tax',
          '236G/H %',
          '236G/H Amount',
        ];

        const numericColumns = [
          'qty',
          'grossAmount',
          'discountAmount',
          'netAmount',
          'salesTaxAmount',
          'furtherTaxAmount',
          'addSalesTaxAmount',
          'valWithoutTax',
          'totalTax',
          'valIncludingTax',
        ];

        const columnIndex = col;
        const columnTitle = columnTitles[columnIndex];
        const columnKey = getColumns()[columnIndex]?.data;

        if (!columnKey) return;

        if (numericColumns.includes(columnKey) && totals[columnKey] !== undefined) {
          const totalValue = formatNumber(totals[columnKey]);
          TH.innerHTML = `
            <div class="column-header-container">
              <div class="column-title">${columnTitle}</div>
              <div class="column-header-total">
                ${totalValue}
              </div>
            </div>
          `;
        } else {
          TH.innerHTML = `
            <div class="column-header-container">
              <div class="column-title">${columnTitle}</div>
            </div>
          `;
        }
      },
      afterRemoveRow: function (index, amount) {
        // Remove rows
        tableData.value.splice(index, amount);
      },
    });

    return true;
  };

  // Row management functions
  const addNewRow = (row) => {
    tableData.value.push(row || getEmptyRow());
    if (hotInstance.value) {
      hotInstance.value.loadData(tableData.value);
    }
  };

  const replaceLastEmptyRow = (row) => {
    // Find the first row with null itemId
    let firstEmptyRowIndex = -1;
    for (let i = 0; i < tableData.value.length; i++) {
      if (tableData.value[i].itemId === null) {
        firstEmptyRowIndex = i;
        break;
      }
    }

    if (firstEmptyRowIndex !== -1) {
      // Replace the first empty row with the new item data using Vue.set for reactivity
      tableData.value.splice(firstEmptyRowIndex, 1, row);
      console.log('Replaced empty row at index:', firstEmptyRowIndex);
    } else {
      // If no empty row found, add a new row
      console.log('No empty row found, adding new row');
      addNewRow(row);
    }

    // Update the handsontable instance
    if (hotInstance.value) {
      hotInstance.value.loadData(tableData.value);
    }
  };

  // Event handlers
  const onItemCreated = (item) => {
    console.log('=== onItemCreated', item);
    showCreateItemModal.value = false;

    const row = {
      itemId: item.id,
      itemHSCode: item.meta_HSCode,
      qty: 0,
      unit: item.meta_unit,
      uom: item.unitID,
      rate: item.retailPrice,
      discountPercent: 0,
      discountAmount: 0,
      salesTaxPercent: item.salesTaxPercent,
      salesTaxAmount: 0,
      sroScheduleNo: '',
      sroItemSerialNo: '',
      notifiedValue: 0,
      furtherTaxPercent: initialFurtherTaxPercent.value,
      furtherTaxAmount: 0,
      addSalesTaxPercent: 0,
      addSalesTaxAmount: 0,
      tax236GValue: 0,
      valIncludingTax: 0,
      item: {
        id: item.id,
        name: item.itemName,
        HSCode: item.meta_HSCode,
        unit: item.meta_unit,
        unitID: item.unitID,
        tradePrice: item.tradePrice,
        retailPrice: item.retailPrice,
        salesTaxPercent: item.salesTaxPercent,
        addSalesTaxPercent: 0,
      },
    };

    // Find the last row with null itemId and replace it
    replaceLastEmptyRow(row);
  };

  const toggleAddTaxCol = () => {
    showAddTaxCol.value = !showAddTaxCol.value;
  };

  // Emit table data
  const emitTableData = (newVal) => {
    emit(
      'input',
      newVal.map((i) => ({
        id: 0,
        businessID: businessID.value,
        itemId: i.item.id,
        itemHSCode: i.itemHSCode,
        qty: i.qty,
        unit: i.unit,
        uom: i.uom,
        rate: i.rate,
        grossAmount: i.grossAmount,
        discountPercent: i.discountPercent,
        discountAmount: i.discountAmount,
        netAmount: i.netAmount,
        salesTaxPercent: i.salesTaxPercent,
        salesTaxAmount: i.salesTaxAmount,
        sroScheduleNo: i.sroScheduleNo,
        sroItemSerialNo: i.sroItemSerialNo,
        notifiedValue: i.notifiedValue,
        furtherTaxPercent: i.furtherTaxPercent,
        furtherTaxAmount: i.furtherTaxAmount,
        addSalesTaxPercent: i.addSalesTaxPercent,
        addSalesTaxAmount: i.addSalesTaxAmount,
        valWithoutTax: i.valWithoutTax,
        totalTax: i.totalTax,
        tax236GValue: i.tax236GValue,
        valIncludingTax: i.valIncludingTax,
      }))
    );
  };

  // Watchers
  watch(
    () => showAddTaxCol.value,
    (newVal) => {
      if (hotInstance.value) {
        hotInstance.value.updateSettings({
          hiddenColumns: {
            columns: newVal ? [] : [16, 17],
          },
        });
        hotInstance.value.render();
      }
    }
  );

  watch(
    () => tableData.value,
    (newVal) => {
      console.log('=== WATCH tableData', JSON.stringify(newVal, null, 2));

      emitTableData(newVal.filter((i) => 'item' in i && i.item.id !== null));
    },
    {
      deep: true,
    }
  );

  // Initialize on mount
  onMounted(async () => {
    setTimeout(() => {
      try {
        initTable();
      } catch (error) {
        console.error('Error', error);
      }
    }, 1000);
  });

  return {
    // Reactive references
    tableRef,
    tableData,
    showCreateItemModal,
    showAddTaxCol,

    // Methods
    toggleAddTaxCol,
    addNewRow,
    onItemCreated,
  };
}
